import type { SourceInputDraft } from "@/lib/scenarios/sourceDraft";
import type { GenerateSourceItem } from "@/lib/scenarios/generateClient";
import { powtoonBE } from "@/lib/powtoon-be/client";
import { ensureGeminiFile, uploadDraftFileToGemini } from "@/lib/scenarios/geminiFilesClient";


const MAX_INLINE_FILE_BYTES = 6 * 1024 * 1024;
/** Safety margin before Gemini's 48-hour expiry. */
const EXPIRY_MARGIN_MS = 2 * 60 * 60 * 1000;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(blob);
  });
}

export function guessMime(name: string, fallback?: string): string {
  if (fallback) return fallback;
  const ext = name.slice(name.lastIndexOf(".") + 1).toLowerCase();
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "pptx":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case "md":
      return "text/markdown";
    case "txt":
      return "text/plain";
    case "csv":
      return "text/csv";
    case "json":
      return "application/json";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    default:
      return "application/octet-stream";
  }
}

export function mimeUsesGeminiFiles(mime: string, name: string): boolean {
  const lower = mime.toLowerCase();
  if (lower === "application/pdf" || lower.startsWith("image/")) return true;
  // Gemini Files API can stage large text files, but DOCX/PPTX still need server-side extraction.
  if (lower === "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
    return false;
  if (lower === "application/vnd.openxmlformats-officedocument.presentationml.presentation")
    return false;
  return false;
}

function isCacheValid(item: { gemini_file_uri?: string; gemini_expires_at?: string }): boolean {
  if (!item.gemini_file_uri || !item.gemini_expires_at) return false;
  const expiresAt = new Date(item.gemini_expires_at).getTime();
  return !Number.isNaN(expiresAt) && expiresAt > Date.now() + EXPIRY_MARGIN_MS;
}

export const MAX_GEMINI_PROXY_BYTES = 20 * 1024 * 1024;

export async function uploadFileToGemini(
  it: Extract<SourceInputDraft, { kind: "upload" }>,
): Promise<{ file_uri: string; expires_at: string }> {
  const file = it._file;
  if (!file) throw new Error(`Attachment "${it.file_name}" is not available`);
  const mime = guessMime(it.file_name, it.mime || file.type || undefined);
  if (file.size > MAX_GEMINI_PROXY_BYTES) {
    throw new Error(
      `"${it.file_name}" is larger than ${Math.round(MAX_GEMINI_PROXY_BYTES / 1024 / 1024)} MB. Save the scenario first so it can be staged from storage.`,
    );
  }
  const uploaded = await uploadDraftFileToGemini(file, it.file_name, mime);
  return { file_uri: uploaded.file_uri, expires_at: uploaded.expires_at };
}


async function ensureAttachmentInGemini(
  it: Extract<SourceInputDraft, { kind: "upload" }>,
  scenarioId: string,
): Promise<{ file_uri: string; expires_at: string }> {
  if (!it.object_id) throw new Error(`Attachment "${it.file_name}" has no object_id`);
  const mime = guessMime(it.file_name, it.mime || undefined);
  const ensured = await ensureGeminiFile({
    scenario_id: scenarioId,
    object_id: it.object_id,
    mime,
    file_name: it.file_name,
  });
  return { file_uri: ensured.file_uri, expires_at: ensured.expires_at };
}

/**
 * Map a single upload draft to a wire item.
 * - PDFs and images go through the Gemini Files API (no base64, no size cap).
 * - DOCX/PPTX and text files keep the base64 path so the edge function can
 *   extract / parse them server-side.
 */
async function resolveUploadDraft(
  it: Extract<SourceInputDraft, { kind: "upload" }>,
  scenarioId?: string,
): Promise<GenerateSourceItem> {
  const mime = guessMime(it.file_name, it.mime || it._file?.type || undefined);

  if (mimeUsesGeminiFiles(mime, it.file_name)) {
    // Reuse cached URI if still valid.
    if (isCacheValid(it)) {
      return {
        kind: "file_uri",
        file_name: it.file_name,
        mime_type: mime,
        file_uri: it.gemini_file_uri!,
        size: it.size ?? it._file?.size,
      };
    }

    // Either upload directly from the local File or ensure from persisted storage.
    let result: { file_uri: string; expires_at: string };
    if (it._file) {
      result = await uploadFileToGemini(it);
    } else if (it.object_id && scenarioId) {
      result = await ensureAttachmentInGemini(it, scenarioId);
    } else {
      throw new Error(`Attachment "${it.file_name}" is not available`);
    }

    // Mutate the draft so subsequent calls (and saveScenario) can reuse the URI.
    it.gemini_file_uri = result.file_uri;
    it.gemini_expires_at = result.expires_at;
    it.gemini_upload_state = "ready";

    return {
      kind: "file_uri",
      file_name: it.file_name,
      mime_type: mime,
      file_uri: result.file_uri,
      size: it.size ?? it._file?.size,
    };
  }

  // Base64 fallback for DOCX/PPTX/text and anything Gemini cannot read natively.
  let blob: Blob;
  if (it._file) {
    if (it._file.size > MAX_INLINE_FILE_BYTES) {
      throw new Error(
        `"${it.file_name}" is ${(it._file.size / 1024 / 1024).toFixed(1)} MB — max ${MAX_INLINE_FILE_BYTES / 1024 / 1024} MB for this file type.`,
      );
    }
    blob = it._file;
  } else if (it.object_id && scenarioId) {
    const att = await powtoonBE.attachments.get(scenarioId, it.object_id);
    const url = att?.file ?? att?.file_url;
    if (!url) throw new Error(`Attachment "${it.file_name}" has no downloadable URL`);
    let res: Response;
    try {
      res = await fetch(url);
    } catch (e) {
      const msg = (e as Error).message || "";
      if (/Failed to fetch|NetworkError|CORS/i.test(msg)) {
        throw new Error(
          `Cannot download "${it.file_name}" — the storage URL may have expired or be CORS-blocked. Re-upload the file.`,
        );
      }
      throw new Error(`Failed to fetch attachment "${it.file_name}": ${msg}`);
    }
    if (!res.ok) {
      throw new Error(
        res.status === 401 || res.status === 403
          ? `Attachment "${it.file_name}" is no longer accessible (HTTP ${res.status}). Re-upload the file.`
          : res.status === 404
            ? `Attachment "${it.file_name}" was not found in storage. Re-upload the file.`
            : `Failed to download "${it.file_name}" (HTTP ${res.status}).`,
      );
    }
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("text/html") && !/\.html?$/i.test(it.file_name)) {
      throw new Error(
        `"${it.file_name}" returned an HTML page (likely an auth redirect or expired link). Re-upload the file.`,
      );
    }
    blob = await res.blob();
    if (blob.size > MAX_INLINE_FILE_BYTES) {
      throw new Error(
        `"${it.file_name}" is ${(blob.size / 1024 / 1024).toFixed(1)} MB — max ${MAX_INLINE_FILE_BYTES / 1024 / 1024} MB for this file type.`,
      );
    }
  } else {
    throw new Error(`Attachment "${it.file_name}" is not available`);
  }

  const data_base64 = await blobToBase64(blob);
  return {
    kind: "upload",
    file_name: it.file_name,
    mime_type: guessMime(it.file_name, it.mime || blob.type || undefined),
    data_base64,
    size: it.size ?? blob.size,
  };
}

/** Map all drafts into wire-ready items. Throws per-file errors verbatim. */
export async function resolveSourceInputs(
  drafts: SourceInputDraft[],
  scenarioId?: string,
): Promise<GenerateSourceItem[]> {
  const results = await Promise.all(
    drafts.map(async (it): Promise<GenerateSourceItem | null> => {
      if (it.kind === "upload") return resolveUploadDraft(it, scenarioId);
      if (it.kind === "url") return it.url.trim() ? { kind: "url", url: it.url.trim() } : null;
      return it.raw_text.trim() ? { kind: "text", raw_text: it.raw_text } : null;
    }),
  );
  return results.filter((x): x is GenerateSourceItem => x !== null);
}
